import React, { useState, useRef, useMemo } from 'react';
import { RoundedBox, Text, Html } from '@react-three/drei';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';

const NetworkBlade = ({ position, type, status, details, onClick }) => {
    const mesh = useRef();
    const [hovered, setHovered] = useState(false);

    // Color Logic
    const getStatusColor = (s) => {
        switch (s) {
            case 'Critical': return '#ef4444'; // Red
            case 'Legacy': return '#f59e0b'; // Amber
            case 'Active': return '#0ea5e9'; // Blue/Teal
            default: return '#64748b'; // Slate
        }
    };

    const baseColor = getStatusColor(status);

    // Pulse Effect for Critical/Legacy items
    useFrame((state) => {
        if (status === 'Critical' || status === 'Legacy') {
            const time = state.clock.getElapsedTime();
            const intensity = (Math.sin(time * 3) + 1) / 2; // 0 to 1 pulse
            if (mesh.current) {
                mesh.current.material.emissiveIntensity = 0.5 + (intensity * 0.5);
            }
        }
    });

    // Geometry based on Type
    const isAP = type === 'AP';
    const U_HEIGHT = 0.15;
    const height = (details.height_u || 1) * U_HEIGHT;

    // Pre-calculate randomized twinkling port states as fallback for devices without per-port config
    const blinkPattern = useMemo(() => {
        return Array.from({ length: 48 }, () => Math.random() > 0.4);
    }, []);

    // Helper: check if a port is active based on portOverrides
    const isPortActive = (bankIndex, portIndex, bank) => {
        if (bank.portOverrides && bank.portOverrides[portIndex]) {
            return bank.portOverrides[portIndex].status === 'active';
        }
        return false; // Default to inactive when no override set
    };

    // Helper: get port label from overrides
    const getPortLabel = (portIndex, bank) => {
        if (bank.portOverrides && bank.portOverrides[portIndex]) {
            return bank.portOverrides[portIndex].label || '';
        }
        return '';
    };

    // AP Visual (Disc)
    if (isAP) {
        return (
            <group position={position}>
                <mesh
                    ref={mesh}
                    onPointerOver={() => setHovered(true)}
                    onPointerOut={() => setHovered(false)}
                    onClick={(e) => { e.stopPropagation(); onClick(details); }}
                >
                    <cylinderGeometry args={[0.3, 0.3, 0.05, 32]} />
                    <meshStandardMaterial
                        color="#ffffff"
                        emissive={baseColor}
                        emissiveIntensity={hovered ? 0.8 : 0.2}
                        roughness={0.2}
                    />
                </mesh>
                {/* Status Ring */}
                <mesh position={[0, 0, 0]} rotation={[Math.PI / 2, 0, 0]}>
                    <ringGeometry args={[0.32, 0.35, 32]} />
                    <meshBasicMaterial color={baseColor} side={THREE.DoubleSide} />
                </mesh>
            </group>
        );
    }

    // ISP / Modem Visual — Small external box (not rack-mounted)
    const isISP = type === 'ISP' || type === 'Modem';
    if (isISP) {
        // Calculate ports based on specs or fallback to 4
        let portElements = [];
        let totalPortsToRender = 0;
        
        if (details.specs?.portBanks && Array.isArray(details.specs.portBanks) && details.specs.portBanks.length > 0) {
            // Render ports according to banks
            details.specs.portBanks.forEach((bank, bIdx) => {
                for (let i = 0; i < (bank.count || 0); i++) {
                    const portActive = isPortActive(bIdx, i, bank);
                    const portLabel = getPortLabel(i, bank) || `Port ${i + 1}`;
                    
                    portElements.push(
                        <mesh key={`modem-port-${bIdx}-${i}`} position={[totalPortsToRender * 0.06, 0, 0]}>
                            <boxGeometry args={[0.04, 0.03, 0.01]} />
                            <meshStandardMaterial
                                color="#0f172a"
                                emissive="#10b981"
                                emissiveIntensity={status === 'Active' && portActive ? 1.5 : 0}
                            />
                            {/* Hover tooltip for ports */}
                            {hovered && (
                                <Html position={[0, 0.05, 0]} center>
                                    <div className="bg-slate-800 text-white text-[10px] px-2 py-1 rounded shadow-lg whitespace-nowrap border border-slate-700 pointer-events-none">
                                        {portLabel}
                                    </div>
                                </Html>
                            )}
                        </mesh>
                    );
                    totalPortsToRender++;
                }
            });
        } else {
            // Hardcoded fallback if no banks configured
            for (let i = 0; i < 4; i++) {
                portElements.push(
                    <mesh key={`modem-port-fallback-${i}`} position={[i * 0.06, 0, 0]}>
                        <boxGeometry args={[0.04, 0.03, 0.01]} />
                        <meshStandardMaterial
                            color="#0f172a"
                            emissive="#10b981"
                            emissiveIntensity={status === 'Active' && blinkPattern[i] ? 1 : 0}
                        />
                    </mesh>
                );
                totalPortsToRender++;
            }
        }

        // Center the port group dynamically based on how many ports there are
        const portsWidth = (totalPortsToRender - 1) * 0.06;
        const startX = -(portsWidth / 2);

        return (
            <group position={position}>
                {/* Small modem/ONT box */}
                <RoundedBox
                    ref={mesh}
                    args={[1.0, 0.12, 0.5]} // slightly wider to accommodate text
                    radius={0.03}
                    smoothness={4}
                    onPointerOver={() => setHovered(true)}
                    onPointerOut={() => setHovered(false)}
                    onClick={(e) => { e.stopPropagation(); onClick(details); }}
                >
                    <meshStandardMaterial
                        color="#e2e8f0"
                        roughness={0.3}
                        metalness={0.05}
                        emissive={hovered ? '#60a5fa' : '#000000'}
                        emissiveIntensity={hovered ? 0.3 : 0}
                    />
                </RoundedBox>
                {/* Ports on front of Modem */}
                <group position={[startX + 0.25, -0.01, 0.252]}>
                    {portElements}
                </group>
                {/* Type label — top of front face */}
                <Text
                    position={[-0.25, 0.03, 0.26]}
                    fontSize={0.04}
                    color="#94a3b8"
                    anchorX="center"
                    anchorY="middle"
                >
                    {type}
                </Text>
                {/* Model name — bottom of front face */}
                <Text
                    position={[-0.25, -0.02, 0.26]}
                    fontSize={0.06}
                    color="#1e293b"
                    anchorX="center"
                    anchorY="middle"
                    fontWeight="bold"
                >
                    {details.name}
                </Text>
            </group>
        );
    }

    // Rack Unit Visual - CLEAN SILVER AESTHETIC (Dynamic Color)
    const isActive = status === 'Active';
    const chassisColor = isActive ? '#f1f5f9' : baseColor; // Silver if active, otherwise Status Color
    const textColor = isActive ? '#1e293b' : '#ffffff'; // Dark text on silver, White on colored

    // Helper to render beautiful visual ports for different gear types
    const renderFrontPanelDetails = () => {
        const elements = [];
        const Z_OFFSET = 0.901; // Front face Z — same for all types (patch panel chassis is pushed forward to align)

        // Materials for different port types
        const getPortMaterial = (pType, isActiveLink) => {
            const baseMat = { roughness: 0.9 };

            if (pType === 'Console') return <meshStandardMaterial color="#334155" {...baseMat} />;
            if (pType === 'SFP') {
                return isActiveLink
                    ? <meshStandardMaterial color="#0f172a" emissive="#3b82f6" emissiveIntensity={1.5} {...baseMat} />
                    : <meshStandardMaterial color="#0f172a" {...baseMat} />;
            }
            if (pType === 'POE') {
                return isActiveLink
                    ? <meshStandardMaterial color="#0f172a" emissive="#f59e0b" emissiveIntensity={1.5} {...baseMat} />
                    : <meshStandardMaterial color="#451a03" {...baseMat} />;
            }
            if (pType === 'WAN') {
                return isActiveLink
                    ? <meshStandardMaterial color="#0f172a" emissive="#ef4444" emissiveIntensity={1.5} {...baseMat} />
                    : <meshStandardMaterial color="#450a0a" {...baseMat} />;
            }
            // Patch Panel ports — lighter so they're visible
            if (type === 'Patch Panel' || pType === 'Normal') {
                return <meshStandardMaterial color="#475569" emissive="#94a3b8" emissiveIntensity={0.15} roughness={0.7} />;
            }

            // Default LAN
            return isActiveLink
                ? <meshStandardMaterial color="#0f172a" emissive="#10b981" emissiveIntensity={1.5} {...baseMat} />
                : <meshStandardMaterial color="#0f172a" {...baseMat} />;
        };

        const getPortGeometry = () => {
            return <boxGeometry args={[0.04, 0.03, 0.01]} />;
        };

        // Fallback for Servers/NAS if no port banks are configured
        if ((type === 'Server' || type === 'NAS') && (!details.specs?.portBanks || details.specs.portBanks.length === 0)) {
            for (let col = 0; col < 8; col++) {
                const x = 0.2 + (col * 0.09);
                elements.push(
                    <group key={`drive-${col}`} position={[x, 0, Z_OFFSET]}>
                        <mesh position={[0, 0, 0]}>
                            <boxGeometry args={[0.07, 0.1, 0.01]} />
                            <meshStandardMaterial color="#334155" roughness={0.7} />
                        </mesh>
                        {isActive && (
                            <mesh position={[-0.02, -0.03, 0.006]}>
                                <circleGeometry args={[0.005, 8]} />
                                <meshBasicMaterial color={blinkPattern[col] ? "#10b981" : "#3b82f6"} />
                            </mesh>
                        )}
                    </group>
                );
            }
            return <group>{elements}</group>;
        }

        // Patch Panel: distribute all ports edge-to-edge across the full chassis width
        if (type === 'Patch Panel' && details.specs?.portBanks && Array.isArray(details.specs.portBanks)) {
            const USABLE_WIDTH = 1.72; // chassis is 2 units, leave ~0.14 total margin
            const totalPorts = details.specs.portBanks.reduce((sum, b) => sum + (b.count || 0), 0);
            if (totalPorts > 0) {
                const portSpacing = USABLE_WIDTH / totalPorts;
                const startX = -(USABLE_WIDTH / 2) + portSpacing / 2;
                let portIdx = 0;
                details.specs.portBanks.forEach((bank, bIdx) => {
                    for (let i = 0; i < (bank.count || 0); i++) {
                        const x = startX + portIdx * portSpacing;
                        elements.push(
                            <mesh key={`pp-${bIdx}-${i}`} position={[x, 0, Z_OFFSET]}>
                                <boxGeometry args={[portSpacing * 0.7, 0.03, 0.01]} />
                                {getPortMaterial(bank.type, false)}
                            </mesh>
                        );
                        portIdx++;
                    }
                });
            }
            return <group>{elements}</group>;
        }

        // Render Dynamic Port Banks — zone-based with collision avoidance
        if (details.specs?.portBanks && Array.isArray(details.specs.portBanks)) {
            // Group banks by their xOffset to detect collisions
            const banksByOffset = {};
            details.specs.portBanks.forEach((bank, origIdx) => {
                const key = String(bank.xOffset || 0);
                if (!banksByOffset[key]) banksByOffset[key] = [];
                banksByOffset[key].push({ bank, origIdx });
            });

            // For each group, calculate per-bank placement
            const bankPositions = {}; // origIdx -> finalBaseX
            Object.values(banksByOffset).forEach(group => {
                const GAP = 0.07; // gap between adjacent banks
                const widths = group.map(({ bank }) => {
                    const colsUsed = Math.min(bank.count || 0, bank.columns || 1);
                    return colsUsed * 0.06; // all ports uniform 0.06 spacing
                });
                const totalWidth = widths.reduce((s, w) => s + w, 0) + GAP * (group.length - 1);

                // Center the entire group around the zone's offset
                // Adjust by +0.03 so baseX represents the center of the first port, not the left edge
                let cursor = ((group[0].bank.xOffset || 0) * 1.3) - (totalWidth / 2) + 0.03;

                group.forEach(({ origIdx }, i) => {
                    bankPositions[origIdx] = cursor;
                    cursor += widths[i] + GAP;
                });
            });

            // Now render each bank at its computed position
            details.specs.portBanks.forEach((bank, bIdx) => {
                const maxCols = bank.columns || 1;
                const totalPorts = bank.count || 0;
                const xSpacing = 0.06;
                const ySpacing = 0.05;
                const baseX = bankPositions[bIdx];
                const Y_RAISE = 0.02;

                for (let i = 0; i < totalPorts; i++) {
                    const row = Math.floor(i / maxCols);
                    const col = i % maxCols;

                    const finalX = baseX + (col * xSpacing);
                    const finalY = -(row * ySpacing) + Y_RAISE;

                const portType = (bank.portOverrides && bank.portOverrides[i])
                        ? bank.portOverrides[i].type
                        : bank.type;

                    // Use real port state from overrides
                    const portActive = isPortActive(bIdx, i, bank);

                    elements.push(
                        <mesh key={`bank-${bIdx}-port-${i}`} position={[finalX, finalY, Z_OFFSET]}>
                            {getPortGeometry(portType)}
                            {getPortMaterial(portType, portActive)}
                        </mesh>
                    );
                }
            });
        }

        return <group>{elements}</group>;
    };

    // Patch Panel: flat face plate, not deep like switches/gateways
    const isPatchPanel = type === 'Patch Panel';
    const chassisDepth = isPatchPanel ? 0.3 : 1.8;
    const chassisZOffset = isPatchPanel ? (1.8 - 0.3) / 2 : 0; // push forward so front face stays aligned
    const finalChassisColor = isPatchPanel ? '#334155' : chassisColor; // medium slate for visibility
    const finalTextColor = isPatchPanel ? '#cbd5e1' : textColor;
    const finalRoughness = isPatchPanel ? 0.7 : 0.5;
    const finalMetalness = isPatchPanel ? 0.05 : 0.1;

    return (
        <group position={position}>
            {/* The Blade Chassis */}
            <RoundedBox
                ref={mesh}
                args={[2, height, chassisDepth]}
                radius={0.05}
                smoothness={4}
                position={[0, 0, chassisZOffset]}
                onPointerOver={() => setHovered(true)}
                onPointerOut={() => setHovered(false)}
                onClick={(e) => {
                    e.stopPropagation();
                    onClick(details);
                }}
            >
                <meshStandardMaterial
                    color={finalChassisColor}
                    roughness={finalRoughness}
                    metalness={finalMetalness}
                    emissive={isPatchPanel ? '#1e293b' : baseColor}
                    emissiveIntensity={hovered ? 0.3 : (isPatchPanel ? 0.1 : (isActive ? 0 : 0.2))}
                />
            </RoundedBox>

            {/* Injected Detailed Port Geometry */}
            {renderFrontPanelDetails()}

            {/* Label */}
            {isPatchPanel ? (
                <Text
                    position={[0, height / 2 + 0.03, 0.91]}
                    fontSize={0.06}
                    color={finalTextColor}
                    anchorX="center"
                    anchorY="middle"
                >
                    {details.name}
                </Text>
            ) : (
                hovered && (
                    <Html position={[0, height / 2, 0.9]} center style={{ pointerEvents: 'none' }}>
                        <div className="bg-[#0f172a]/95 backdrop-blur-md border border-white/10 text-white/90 text-[10px] font-bold px-2.5 py-1 rounded-full shadow-2xl whitespace-nowrap transform -translate-y-[120%] tracking-wide flex items-center gap-1.5">
                            <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: baseColor }} />
                            {details.name}
                        </div>
                    </Html>
                )
            )}
        </group>
    );
};

export default NetworkBlade;
